import { ShieldOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Capability } from '@jt/domain';
import { useSession } from '../lib/session';

/**
 * Refuses to render a page the signed-in role has no business seeing.
 *
 * Hiding a link in the sidebar is presentation, not a boundary — typing the
 * address still rendered the page, and a page that renders and then fails
 * every request looks like a broken app rather than a closed door.
 *
 * This is not the security control. The API checks the same capability on
 * every request and is the copy that counts; this exists so the answer is an
 * honest one instead of a screen full of red.
 */
export function Guard({
  capability,
  children,
}: {
  capability: Capability;
  children: React.ReactNode;
}) {
  const { can } = useSession();

  if (can(capability)) return <>{children}</>;

  return (
    <div className="mx-auto max-w-md py-16 text-center">
      <ShieldOff className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden="true" />
      <h2 className="mt-4 font-heading text-xl">Not your area</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        This page is restricted, and your account does not have access to it. If you think it
        should, ask an administrator.
      </p>
      <Link to="/" className="mt-6 inline-block text-sm text-primary-600 hover:underline">
        Back to work
      </Link>
    </div>
  );
}
